import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { adminAPI, Task, User } from '../api/api';
import Navbar from './Navbar';
import CreateTaskModal from './CreateTaskModal';
import TaskModal from './TaskModal';
import './UserDetails.css';

const UserDetails: React.FC = () => {
  const { id } = useParams();
  const userId = Number(id);

  const [user, setUser] = useState<User | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [filter, setFilter] = useState<'all' | 'open' | 'completed' | 'in_review'>('all');
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);

  useEffect(() => {
    if (!userId) return;

    const load = async () => {
      try {
        setLoading(true);
        const [users, userTasks] = await Promise.all([
          adminAPI.getAllUsers(),
          adminAPI.getUserTasks(userId),
        ]);
        const current = users.find((u) => u.id === userId) || null;
        setUser(current);
        setTasks(userTasks);
      } catch (error) {
        console.error('Ошибка при загрузке данных пользователя:', error);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [userId]);

  const stats = useMemo(() => {
    const total = tasks.length;
    const open = tasks.filter((t) => t.status === 'open').length;
    const completed = tasks.filter((t) => t.status === 'completed').length;
    const inReview = tasks.filter((t) => t.status === 'in_review').length;
    return { total, open, completed, inReview };
  }, [tasks]);

  const filteredTasks = useMemo(() => {
    if (filter === 'all') return tasks;
    return tasks.filter((task) => task.status === filter);
  }, [tasks, filter]);

  const handleTaskCreated = async () => {
    setShowCreateTask(false);
    if (!userId) return;
    try {
      const userTasks = await adminAPI.getUserTasks(userId);
      setTasks(userTasks);
    } catch (error) {
      console.error('Ошибка при обновлении задач пользователя:', error);
    }
  };

  const handleTaskUpdated = async () => {
    if (!userId) return;
    try {
      const userTasks = await adminAPI.getUserTasks(userId);
      setTasks(userTasks);
    } catch (error) {
      console.error('Ошибка при обновлении задач пользователя:', error);
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="loading">Загрузка...</div>
      </>
    );
  }

  if (!user) {
    return (
      <>
        <Navbar />
        <div className="loading">Пользователь не найден</div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="user-details-container">
        <div className="user-header">
          <div className="user-header-main">
            <div className="user-avatar-circle large">
              {user.username
                .split(' ')
                .map((p) => p[0])
                .join('')
                .toUpperCase()
                .slice(0, 2)}
            </div>
            <div>
              <h1>{user.username}</h1>
              <div className="user-header-meta">
                <span>{user.email}</span>
                <span>Должность: {user.position || 'Не указана'}</span>
                <span>
                  Руководящая должность: {user.is_manager ? 'Да' : 'Нет'}
                </span>
                <span>
                  Статус: {user.approved ? '✅ Подтвержден' : '⏳ Ожидает подтверждения'}
                </span>
              </div>
            </div>
          </div>
          <button
            className="primary-btn"
            onClick={() => setShowCreateTask(true)}
          >
            + Поставить задачу
          </button>
        </div>

        <div className="user-stats">
          <div className="stat-card">
            <div className="stat-value">{stats.total}</div>
            <div className="stat-label">Всего задач</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.open}</div>
            <div className="stat-label">Открытые</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.inReview}</div>
            <div className="stat-label">На проверке</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.completed}</div>
            <div className="stat-label">Завершенные</div>
          </div>
        </div>

        <div className="user-tasks-section">
          <div className="user-tasks-header">
            <h2>Задачи сотрудника</h2>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as 'all' | 'open' | 'completed' | 'in_review')}
              className="tasks-filter-select"
            >
              <option value="all">Все</option>
              <option value="open">Открытые</option>
              <option value="in_review">На проверке</option>
              <option value="completed">Завершенные</option>
            </select>
          </div>
          {tasks.length === 0 ? (
            <div className="empty-state">
              У пользователя пока нет задач. Нажмите «Поставить задачу», чтобы создать первую.
            </div>
          ) : filteredTasks.length === 0 ? (
            <div className="empty-state">
              Нет задач с выбранным статусом
            </div>
          ) : (
            <>
              {/* Таблица для десктопа */}
              <table className="user-tasks-table">
                <thead>
                  <tr>
                    <th>Проект</th>
                    <th>Этап</th>
                    <th>Название</th>
                    <th>Статус</th>
                    <th>Создана</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTasks.map((task) => (
                    <tr 
                      key={task.id}
                      onClick={() => setSelectedTaskId(task.id)}
                      className="task-row-clickable"
                    >
                      <td>{(task as any).board_name || '—'}</td>
                      <td>{(task as any).column_name || '—'}</td>
                      <td>{task.title}</td>
                      <td>
                        {task.status === 'open'
                          ? 'Открыта'
                          : task.status === 'completed'
                          ? 'Завершена'
                          : task.status === 'in_review'
                          ? 'На проверке'
                          : task.status}
                      </td>
                      <td>{new Date(task.created_at).toLocaleString('ru-RU')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Карточки для мобильных */}
              <div className="user-tasks-mobile-cards">
                {filteredTasks.map((task) => (
                  <div
                    key={task.id}
                    onClick={() => setSelectedTaskId(task.id)}
                    className="user-tasks-mobile-card"
                  >
                    <div className="user-tasks-mobile-card-row">
                      <span className="user-tasks-mobile-card-label">Проект:</span>
                      <span className="user-tasks-mobile-card-value">{(task as any).board_name || '—'}</span>
                    </div>
                    <div className="user-tasks-mobile-card-row">
                      <span className="user-tasks-mobile-card-label">Этап:</span>
                      <span className="user-tasks-mobile-card-value">{(task as any).column_name || '—'}</span>
                    </div>
                    <div className="user-tasks-mobile-card-row">
                      <span className="user-tasks-mobile-card-label">Название:</span>
                      <span className="user-tasks-mobile-card-value">{task.title}</span>
                    </div>
                    <div className="user-tasks-mobile-card-row">
                      <span className="user-tasks-mobile-card-label">Статус:</span>
                      <span className="user-tasks-mobile-card-value">
                        {task.status === 'open'
                          ? 'Открыта'
                          : task.status === 'completed'
                          ? 'Завершена'
                          : task.status === 'in_review'
                          ? 'На проверке'
                          : task.status}
                      </span>
                    </div>
                    <div className="user-tasks-mobile-card-row">
                      <span className="user-tasks-mobile-card-label">Создана:</span>
                      <span className="user-tasks-mobile-card-value">
                        {new Date(task.created_at).toLocaleString('ru-RU')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {showCreateTask && (
        <CreateTaskModal
          onClose={() => setShowCreateTask(false)}
          onTaskCreated={handleTaskCreated}
          defaultAssignedUserId={userId}
        />
      )}

      {selectedTaskId && (
        <TaskModal
          taskId={selectedTaskId}
          onClose={() => setSelectedTaskId(null)}
          onTaskUpdated={handleTaskUpdated}
        />
      )}
    </>
  );
};

export default UserDetails;


